import React, { useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { ITicketBillingAddress } from "src/stores/CommunicationSettingsStore";
import styles from "src/screens/communicationSettings/CommunicationSettings.module.css";

const TicketingCommunicationSettingsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { communicationSettingsStore } = useContext(RootStoreContext);
    const { ticketBillingAddress } = communicationSettingsStore;

    const onChangeAddress = (field: keyof ITicketBillingAddress) => (e: React.ChangeEvent<HTMLInputElement>) => {
        communicationSettingsStore.updateTicketBillingAddress(field, e.target.value);
    };

    return (
        <UI.Row>
            <UI.Col md={12}>
                <UI.Card
                    title={t("screens.communication_settings.easycontact_ticketing.ticketing.title").toString()}
                    className={styles.SettingsContainer}
                >
                    <UI.Row className="mb-3">
                        <UI.Col>
                            <h6 className="bold-text" style={{ color: "var(--primary)" }}>
                                {t("screens.communication_settings.easycontact_ticketing.ticketing.demo_mode.subtitle")}
                            </h6>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row className="mb-3">
                        <UI.Col>
                            <div className="d-flex align-items-center">
                                <UI.Switch
                                    checked={communicationSettingsStore.ticketDemoMode}
                                    onToggle={communicationSettingsStore.toggleTicketDemoMode}
                                />
                                <span className="inline-block ml-2">
                                    {t(
                                        "screens.communication_settings.easycontact_ticketing.ticketing.demo_mode.switch_label"
                                    )}
                                </span>
                            </div>
                        </UI.Col>
                    </UI.Row>
                    <hr />
                    <UI.Row className="mb-3">
                        <UI.Col>
                            <h6 className="bold-text" style={{ color: "var(--primary)" }}>
                                {t("screens.communication_settings.easycontact_ticketing.ticketing.billing.subtitle")}
                            </h6>
                            <span>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.ticketing.billing.explanation_text"
                                )}
                            </span>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row className="align-items-center mb-3">
                        <UI.Col md={4} lg={3}>
                            <label className="m-0 bold-text" style={{ color: "var(--primary)" }}>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.ticketing.billing.email_address"
                                )}
                            </label>
                        </UI.Col>
                        <UI.Col md={8} lg={6} className={styles.InputCol}>
                            <UI.Input
                                type="text"
                                value={communicationSettingsStore.ticketBillingEmail}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    communicationSettingsStore.updateTicketBillingEmail(e.target.value)
                                }
                                errorMsg={communicationSettingsStore.ticketBillingEmailError}
                            />
                        </UI.Col>
                    </UI.Row>
                    <UI.Row className="align-items-start mb-3">
                        <UI.Col md={4} lg={3} className="pt-2">
                            <label className="m-0 bold-text" style={{ color: "var(--primary)" }}>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.ticketing.billing.billing_address"
                                )}
                            </label>
                        </UI.Col>
                        <UI.Col md={8} lg={6}>
                            <UI.Row>
                                <UI.Col>
                                    <UI.Input
                                        label={t("screens.tickets.form.person.name").toString()}
                                        type="text"
                                        value={ticketBillingAddress.name}
                                        onChange={onChangeAddress("name")}
                                    />
                                </UI.Col>
                            </UI.Row>
                            <UI.Row>
                                <UI.Col>
                                    <UI.Input
                                        label={t("screens.tickets.form.person.co").toString()}
                                        type="text"
                                        value={ticketBillingAddress.co}
                                        onChange={onChangeAddress("co")}
                                    />
                                </UI.Col>
                            </UI.Row>
                            <UI.Row>
                                <UI.Col>
                                    <UI.Input
                                        label={t("screens.tickets.form.person.street").toString()}
                                        type="text"
                                        value={ticketBillingAddress.street}
                                        onChange={onChangeAddress("street")}
                                    />
                                </UI.Col>
                                <UI.Col>
                                    <UI.Input
                                        label={t("screens.tickets.form.person.street_number").toString()}
                                        type="text"
                                        value={ticketBillingAddress.housenumber}
                                        onChange={onChangeAddress("housenumber")}
                                    />
                                </UI.Col>
                            </UI.Row>
                            <UI.Row>
                                <UI.Col>
                                    <UI.Input
                                        label={t("screens.tickets.form.person.zip").toString()}
                                        type="text"
                                        value={ticketBillingAddress.zip}
                                        onChange={onChangeAddress("zip")}
                                    />
                                </UI.Col>
                                <UI.Col>
                                    <UI.Input
                                        label={t("screens.tickets.form.person.city").toString()}
                                        type="text"
                                        value={ticketBillingAddress.city}
                                        onChange={onChangeAddress("city")}
                                    />
                                </UI.Col>
                            </UI.Row>
                        </UI.Col>
                    </UI.Row>
                </UI.Card>
            </UI.Col>
        </UI.Row>
    );
};

export const TicketingCommunicationSettings = withRouter(
    Sentry.withProfiler(observer(TicketingCommunicationSettingsBase))
);
